/* eslint-disable max-lines */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { StyledInsightsWrapper } from './StyledInsightsWrapper';
import { useLocation } from 'react-router-dom';
import { ObjectMap } from 'mf_core/query';
import { useTranslate } from '@im/react-localization';
import { setDigitalData } from 'mf_core/adobe';

type FilterChip = {
  active: boolean;
  count: number;
  name: string;
};

type Prop = {
  additionalInsightsData: ObjectMap;
  currentPage: string;
  insightsData: ObjectMap[];
  isInsightsError: boolean;
  isInsightsLoading: boolean;
  onClickInsightsCenter: (navigateLink?: string) => void;
  widgetTitle: string;
};

export default function InsightsContent(prop: Readonly<Prop>) {
  const {
    additionalInsightsData,
    currentPage,
    insightsData,
    isInsightsError,
    isInsightsLoading,
    onClickInsightsCenter,
    widgetTitle,
  } = prop;

  const translate = useTranslate();
  const [viewAllData, setViewAllData] = useState<boolean>(true);
  const [chipsInsights, setChipsInsights] = useState<ObjectMap[] | null>(null);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);

  const location = useLocation();

  const generateKey = (pre: string, id: any) => {
    return `${pre}_${id}`;
  };

  useEffect(() => {
    setChipsInsights(insightsData ?? null);

    const filterChipList: FilterChip[] = [];
    filterChipList.push({
      active: true,
      count: insightsData?.length ?? 0,
      name: 'View All',
    });

    insightsData?.forEach((chip: ObjectMap) => {
      if (
        !filterChipList.some(
          (filterChip) => filterChip.name === chip.insightTemplateName
        )
      )
        filterChipList.push({
          active: false,
          count: insightsData?.filter(
            (chipCount: ObjectMap) =>
              chipCount.insightTemplateName === chip.insightTemplateName
          )?.length,
          name: chip.insightTemplateName ?? '',
        });
    });
    setFilterChips(filterChipList);
    setViewAllData(true);
  }, [insightsData]);

  const showContent = useCallback(
    (chipName: string) => {
      setFilterChips((prevState) => {
        return prevState.map((chip) => {
          if (chip.name === chipName) {
            return { active: true, count: chip.count, name: chip.name };
          } else {
            return { active: false, count: chip.count, name: chip.name };
          }
        });
      });

      const impressionsData: ObjectMap[] = [];

      const contentDetails = chipsInsights?.map((chipDetails) => {
        if (chipName === chipDetails.insightTemplateName) {
          impressionsData.push({
            InsightName: chipDetails.insightTitle,
            InsightTab: chipDetails.insightTemplateName,
          });
          return {
            ...chipDetails,
            active: true,
          };
        } else {
          if (chipName === 'View All') {
            impressionsData.push({
              InsightName: chipDetails.insightTitle,
              InsightTab: 'View All',
            });
          }
          return {
            ...chipDetails,
            active: false,
          };
        }
      });
      setDigitalData({
        QuickInsightsImpressions: impressionsData,
      });

      setViewAllData(chipName === 'View All');
      setChipsInsights(contentDetails ?? null);
    },
    [chipsInsights]
  );

  useEffect(() => {
    if (insightsData?.length > 0) {
      const impressionsData = insightsData?.map((chipDetails: any) => {
        return {
          InsightName: chipDetails.insightTitle,
          InsightTab: 'View All',
        };
      });

      setDigitalData({ QuickInsightsImpressions: impressionsData });
    }
  }, [insightsData]);

  return (
    <StyledWrapper>
      {isInsightsLoading ? (
        <Stack alignItems="center" height="339px" justifyContent="center">
          <CircularProgress />
        </Stack>
      ) : (
        <Stack direction="column">
          <Stack direction="row" justifyContent="flex-start">
            <Typography sx={{ fontWeight: 'bold' }} variant="body1">
              {widgetTitle}
            </Typography>
            <CustomWidthTooltip title={'Tooltip Text'}>
              <InfoOutlinedIcon
                color="disabled"
                fontSize="small"
                sx={{ display: 'none', ml: 1 }}
              />
            </CustomWidthTooltip>
          </Stack>
          <StyledPageNameTypography variant="body2">
            {translate(`Insights.${currentPage.replace(/\s/g, '')}`)}
          </StyledPageNameTypography>
          <Box component="div" py={2} sx={{ maxWidth: 650 }}>
            <StyledStack direction="row" flexWrap="nowrap" spacing={1}>
              {filterChips.map((filterChip: FilterChip) => {
                return (
                  <StyledChip
                    className={filterChip.active ? 'active' : ''}
                    data-name={`Quick-insights_${filterChip?.name?.replace(
                      ' ',
                      '-'
                    )}`}
                    deleteIcon={
                      <CountBadge className="count-badge">
                        {filterChip?.count}
                      </CountBadge>
                    }
                    key={filterChip?.name}
                    label={
                      filterChip.name === 'View All'
                        ? translate('Insights.ViewAll')
                        : filterChip.name
                    }
                    onClick={() => showContent(filterChip.name)}
                    onDelete={() => showContent(filterChip.name)}
                    variant="outlined"
                  />
                );
              })}
            </StyledStack>
            {insightsData?.length <= 0 || isInsightsError ? (
              <Stack alignItems={'center'} height="339px" pt={6}>
                <Typography variant="h2">
                  {translate('Insights.NoInsightsAvailable')}
                </Typography>
              </Stack>
            ) : (
              <StyledStackContainer
                alignContent={'flex-start'}
                direction="row"
                flexWrap="wrap"
                height="339px"
                overflow="auto"
                spacing={1}
              >
                {/* View All start*/}

                {viewAllData &&
                  chipsInsights?.map((viewAll: ObjectMap, index: number) => {
                    return (
                      <StyledStackRow
                        data-name={`Quick-insights_Insights_${viewAll.insightName}`}
                        key={generateKey('Insight', index)}
                      >
                        <StyledInsightsWrapper
                          additionalInsightsData={additionalInsightsData}
                          bodyContent={viewAll.insightContent}
                          ctaLink={viewAll.cta_link}
                          currentPage={currentPage}
                          dataName={viewAll.dataName}
                          insightTitle={viewAll.insightTitle}
                        />
                      </StyledStackRow>
                    );
                  })}

                {/* End View All */}
                {chipsInsights?.map((filterChip: ObjectMap, index: number) => {
                  return (
                    filterChip.active && (
                      <StyledStackRow
                        data-name={`Quick-insights_Insights_${filterChip.insightName}`}
                        key={generateKey('Insight', index)}
                      >
                        <StyledInsightsWrapper
                          additionalInsightsData={additionalInsightsData}
                          bodyContent={filterChip.insightContent}
                          ctaLink={filterChip.cta_link}
                          currentPage={currentPage}
                          dataName={filterChip.dataName}
                          insightTitle={filterChip.insightTitle}
                        />
                      </StyledStackRow>
                    )
                  );
                })}
              </StyledStackContainer>
            )}

            {location.pathname !== '/insight/insights-v2' && (
              <Stack marginTop={2} position={'absolute'} right={16}>
                <StyledButton
                  data-name="Quick-insights_Insights Center"
                  onClick={() => onClickInsightsCenter()}
                  variant="contained"
                >
                  {translate('Insights.InsightsCenter')}
                </StyledButton>
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </StyledWrapper>
  );
}

const StyledWrapper = styled(Stack)(({ theme }) => ({
  backgroundColor: '#FFF',
  borderRadius: '4px',
  display: 'inline-flex',
  height: 520,
  minWidth: 350,
  padding: theme.spacing(2),
  width: 680,
}));

const StyledButton = styled(Button)(() => ({
  textTransform: 'none',
}));

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    background: '#333333 !important',
    borderRadius: '4px',
    boxShadow: 'none',
    color: '#fff !important',
    fontSize: '12px !important',
  },
});

const StyledChip = styled(Chip)(({ theme }) => ({
  '&.active': {
    '.count-badge': {
      backgroundColor: 'rgba(1, 100, 176, 1)',
      color: '#fff',
    },
    backgroundColor: 'rgba(243, 250, 255, 1)',
    borderColor: 'rgba(0, 119, 212, 1)',
    marginBottom: theme.spacing(0.5),
  },
}));

const CountBadge = styled(Box)(({ theme }) => ({
  '&.MuiChip-deleteIcon, &.MuiChip-deleteIcon:hover': {
    backgroundColor: '#eeeeee',
    borderRadius: 4,
    color: '#333333',
    fontSize: '12px !important',
    padding: theme.spacing(0, 1, 0, 1),
  },
}));

const StyledStack = styled(Stack)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '8.86px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.text.disabled,
    borderRadius: '6px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.primary.main,
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: '6px',
    boxShadow: 'none',
  },
  '&::-webkit-scrollbar-track:hover': {
    boxShadow: 'inset 0 0 6px #C4C4C4',
  },
  height: 46,
  overflowX: 'auto',
  overflowY: 'hidden',
}));

const StyledStackContainer = styled(Stack)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '8.86px',
    width: '8.86px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.text.disabled,
    borderRadius: '6px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.primary.main,
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: '6px',
    boxShadow: 'none',
  },
  '&::-webkit-scrollbar-track:hover': {
    boxShadow: 'inset 0 0 6px #C4C4C4',
  },
}));

const StyledStackRow = styled(Stack)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: '0 !important',
}));

const StyledPageNameTypography = styled(Typography)(({ theme }) => ({
  color: '#a9a8a8',
  fontSize: '11px',
  fontWeight: 'bold',
  lineHeight: '10px',
}));
